/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-observers, ember/no-runloop, ember/require-computed-property-dependencies, getter-return, no-restricted-properties */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { setDiff, mapBy, alias } from '@ember/object/computed';
import { debounce } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import SchoolStatus from '@linc-technologies/linced-common/enums/school-status';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import moment from 'moment';
import NSWStatus from '@linc-technologies/linced-common/enums/nsw-status';
import { observes } from '@ember-decorators/object';
import * as arrays from '@linc-technologies/linced-common/utils/arrays';

export default class ResourcingAnalysisController extends Controller {
  queryParams = ['dateFrom', 'dateTo', 'groupId'];

  @service
  attendanceCodes;

  @service
  session;

  @service
  dates;

  @service
  strings;

  @computed('model.groups.[]')
  get filteredGroups() {
    return this.model.groups
      .filter((group) => {
        if (group.type === GroupType.GROUP) {
          // Omit My Groups which the current user isn't a current staff member in.
          let staff = group.get('currentStaff') ?? [];
          return !!staff.find(
            (item) => item.personId === this.session.personId,
          );
        }
        if (group.status === NSWStatus.INACTIVE) {
          return false;
        }
        // Include all other groups.
        return true;
      })
      .sort(
        (a, b) => a.type - b.type || (a.name || '').localeCompare(b.name || ''),
      );
  }

  @tracked groupId = null;

  get group() {
    if (isEmpty(this.groupId)) {
      return;
    }
    return this.model.groups.find((group) => group.id === this.groupId);
  }

  @tracked dateFrom = null;
  @tracked
  newDateFrom = null;
  @tracked dateTo = null;
  @tracked
  newDateTo = null;

  get year() {
    return moment(this.dateFrom, 'YYYY-MM-DD').year();
  }

  // _datesChanged updates the "new" date properties to keep them in line with the query params.
  @observes('dateFrom', 'dateTo')
  _datesChanged() {
    this.newDateFrom = this.dateFrom;
    this.newDateTo = this.dateTo;
  }

  // updateDates should be run debounced in order to prevent the route's model reloading
  // as soon as a "valid" date is entered, as the first digit of the year will make it "valid".
  updateDates() {
    let props = {};
    let hasNewValue = false;

    if (!isEmpty(this.newDateFrom)) {
      props.dateFrom = this.newDateFrom;
      props.newDateFrom = null;
      hasNewValue = true;
    }

    if (!isEmpty(this.newDateTo)) {
      props.dateTo = this.newDateTo;
      props.newDateTo = null;
      hasNewValue = true;
    }

    if (hasNewValue) {
      this.setProperties(props);
    }
  }

  @alias('attendanceCodes.allCodes')
  codes;

  @tracked codeFilter = [];

  @setDiff('codes', 'codeFilter')
  remainingCodes;

  matchCode(option, code) {
    return (
      option.code === code.toUpperCase() ||
      option.label.toLowerCase().indexOf(code.toLowerCase())
    );
  }

  @mapBy('codeFilter', 'code')
  codeFilterCodes;

  @computed('codes.[]', 'codeFilter.[]')
  get codeColumns() {
    if (!isEmpty(this.codeFilter)) {
      return this.codeFilter;
    }
    return this.codes;
  }

  @computed('model.records.[]', 'codeFilterCodes.[]')
  get filteredLearnerIds() {
    if (isEmpty(this.codeFilterCodes)) {
      return [...new Set(this.model.records.map((item) => item.person_id))];
    }

    // Determine which learners to include.
    return arrays.mapBy(
      arrays.uniqBy(
        this.model.records.filter((record) => {
          return this.codeFilterCodes.includes(record.code);
        }),
        'person_id',
      ),
      'person_id',
    );
  }

  @computed('model.dates.[]', 'dateFrom', 'dateTo')
  get schoolDates() {
    let dates = {};
    let date = moment(this.dateFrom, 'YYYY-MM-DD');
    let endDate = moment(this.dateTo, 'YYYY-MM-DD');
    let openStatuses = [
      SchoolStatus.OPEN,
      SchoolStatus.MORNING,
      SchoolStatus.AFTERNOON,
    ];

    while (date.isSameOrBefore(endDate)) {
      let dateString = date.format('YYYY-MM-DD');
      let schoolDate = this.model.dates.find(
        (item) => item.date === dateString,
      );
      if (!isEmpty(schoolDate) && openStatuses.includes(schoolDate.status)) {
        let day = this.dates.dayForDate(dateString);
        if (!isEmpty(day)) {
          dates[dateString] = day.periodIds;
        }
      }
      date.add(1, 'day');
    }

    return dates;
  }

  @computed('model.records.[]', 'codeFilterCodes.[]', 'filteredLearnerIds.[]')
  get tableData() {
    let unfiltered = false;
    if (isEmpty(this.codeFilterCodes)) {
      unfiltered = true;
    }

    let dayMap = this.schoolDates;
    let rawDates = Object.entries(dayMap).map(([date, periods]) => {
      let codes = [];
      for (let i = 0; i < periods.length; i++) {
        codes.push('-');
      }
      return {
        date,
        codes,
      };
    });
    let rawDatesJSON = JSON.stringify([...rawDates]);

    // Filter records to the learners identified above.
    let data = this.model.records.reduce((data, record) => {
      let learnerId = record.person_id;
      if (unfiltered || this.filteredLearnerIds.includes(learnerId)) {
        if (isEmpty(data[learnerId])) {
          data[learnerId] = {
            person: record.person,
            dates: JSON.parse(rawDatesJSON),
          };
        }

        if (!isEmpty(dayMap[record.date])) {
          let periodIndex = dayMap[record.date].indexOf(record.period_id);
          if (periodIndex > -1) {
            data[learnerId].dates.find(
              (item) => item.date === record.date,
            ).codes[periodIndex] = record.code;
          }
        }
      }
      return data;
    }, {});

    return Object.values(data);
  }

  init() {
    super.init(...arguments);
    this.reset();
  }

  @action
  selectGroup(group) {
    this.groupFilter.pushObject(group);
  }

  @action
  removeGroup(group) {
    this.groupFilter.removeObject(group);
  }

  @action
  selectCode(code) {
    this.codeFilter = [...this.codeFilter, code];
  }

  @action
  removeCode(code) {
    this.codeFilter = this.codeFilter.filter((item) => item !== code);
  }

  @action
  onChangeDateFrom(newDate) {
    this.newDateFrom = newDate;
    debounce(this, 'updateDates', 500);
  }

  @action
  onChangeDateTo(newDate) {
    this.newDateTo = newDate;
    debounce(this, 'updateDates', 500);
  }

  @action
  setGroup(group) {
    if (isEmpty(group)) {
      this.groupId = null;
    } else {
      this.groupId = group.id;
    }
  }

  reset() {
    let lastWeek = moment().subtract(1, 'week').format('YYYY-MM-DD');
    let today = moment().format('YYYY-MM-DD');
    this.setProperties({
      codeFilter: [],
      dateFrom: lastWeek,
      newDateFrom: lastWeek,
      dateTo: today,
      newDateTo: today,
      groupId: null,
    });
  }
}
